import { client } from '@/client';
import { SystemRoles } from '@/client/users';
import { AppRoutesPaths, LoadingStatuses } from '@/common/constants';
import { ImpersonationBanner } from '@/components/auth/ImpersonationBanner';
import { useAppSelector } from '@/hooks/store';
import logo from '@/images/logo-black.svg';
import { selectCurrentUserState } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--white-main);
  padding: 125px 100px 200px 100px;
  position: relative;
  overflow: hidden;
  text-align: center;

  .watermark {
    position: absolute;
    bottom: -40px;
    left: -29px;
    width: 105%;
    opacity: 0.03;
    pointer-events: none;
    z-index: 0;
  }

  .content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 42px;
  }

  .text-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .clock-icon {
    width: 102px;
    height: 102px;
    opacity: 0.3;
  }

  .text-content {
    display: flex;
    flex-direction: column;
    gap: 28px;
    max-width: 613px;
  }

  h1 {
    font-size: 40px;
    line-height: 1.3;
    font-weight: 700;
    margin: 0;
  }

  p {
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
  }
`;

const ClockIcon = () => (
  <svg
    className="clock-icon"
    viewBox="0 0 102 102"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="51" cy="51" r="48" stroke="currentColor" strokeWidth="3" />
    <line
      x1="51"
      y1="20"
      x2="51"
      y2="51"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <line
      x1="51"
      y1="51"
      x2="70"
      y2="51"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

export const ExpiredTrialPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAppSelector(selectCurrentUserState);
  const [extending, setExtending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin =
    !!user?.role?.code &&
    [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
      user.role.code as SystemRoles,
    );

  const deletionDate = user?.account?.freeTrialDeletionDate
    ? moment(user.account.freeTrialDeletionDate).format('LL')
    : '';

  const handleExtendTrial = async () => {
    setExtending(true);
    setError(null);
    try {
      await client.accounts.extendFreeTrial();
      navigate(AppRoutesPaths.BASE);
      window.location.reload();
    } catch {
      setError(t('trialExpired.error.extendFailed'));
      setExtending(false);
    }
  };

  return (
    <>
      <ImpersonationBanner />
      <StyledPage translate="no">
        <img className="watermark" src={logo} alt="" aria-hidden="true" />
        <div className="content">
          <div className="text-group">
            <ClockIcon />
            <div className="text-content">
              {isAdmin ? (
                <>
                  <h1>{t('trialExpired.admin.heading')}</h1>
                  <p>{t('trialExpired.admin.body.dataRetained')}</p>
                  <p>
                    {t('trialExpired.admin.body.deletionDate', {
                      date: deletionDate,
                    })}
                  </p>
                  <p>{t('trialExpired.admin.body.extendInfo')}</p>
                </>
              ) : (
                <>
                  <h1>{t('trialExpired.learner.heading')}</h1>
                  <p>{t('trialExpired.learner.body.noAccess')}</p>
                  <p>{t('trialExpired.learner.body.contactAdmin')}</p>
                </>
              )}
            </div>
          </div>
          {isAdmin && (
            <AppButton
              label={t('trialExpired.admin.button.extend')}
              onClick={handleExtendTrial}
              state={extending ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
              isDisabled={extending}
              style={{
                backgroundColor: 'var(--red-dark)',
                borderColor: 'var(--red-dark)',
                borderRadius: '25px',
                padding: '14px 24px',
              }}
            />
          )}
          {error && (
            <p role="alert" style={{ color: 'var(--red-dark)' }}>
              {error}
            </p>
          )}
        </div>
      </StyledPage>
    </>
  );
};
